import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { formatBytes } from '../lib/api.js';
import {
  fromDirectoryInput,
  fromDrop,
  type UploadCandidate,
} from '../lib/folder-upload.js';
import { useUploads, ACTIVE_STATUSES, CONCURRENCY_OPTIONS } from '../lib/upload-manager.js';
import { useSession, TIER } from '../App.js';
import { FolderPicker } from '../components/FolderPicker.js';

export function UploadPage(): ReactNode {
  const {
    items,
    enqueue,
    retry,
    retryAllFailed,
    cancel,
    cancelAll,
    clearFinished,
    failedCount,
    concurrency,
    setConcurrency,
  } = useUploads();

  const { me } = useSession();
  // Matches the server's folder:create, which is the same tier as uploading.
  // Checked rather than assumed so that if the two ever diverge again, the
  // button is hidden instead of offered to people who can only watch it fail.
  const canCreateFolders = (me?.tier ?? 0) >= TIER.member;

  // "Upload here" on a folder in the grid links to /upload?folder=<id>, so the
  // destination arrives already chosen rather than having to be found again.
  // Kept in the URL as you browse the picker, so a reload mid-batch -- or
  // coming back to this tab later -- lands on the same folder.
  const [searchParams, setSearchParams] = useSearchParams();
  const [folderId, setFolderId] = useState<string>(searchParams.get('folder') ?? '');
  const chooseDestination = (id: string): void => {
    setFolderId(id);
    setSearchParams(id ? { folder: id } : {}, { replace: true });
  };
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  // Walking a dropped tree is async and can take a moment on a large card.
  const [scanning, setScanning] = useState(false);


  const addCandidates = (candidates: UploadCandidate[]): void => {
    enqueue(candidates, folderId || null);
  };


  const active = items.filter((i) => ACTIVE_STATUSES.has(i.status));
  const totalBytes = items.reduce((sum, i) => sum + i.file.size, 0);
  const sentBytes = items.reduce((sum, i) => sum + i.sent, 0);

  /**
   * Rows are capped because every progress tick re-renders the table, and a
   * few thousand files made the page slowest at exactly the moment it had the
   * most to report.
   *
   * The cap is applied in priority order rather than to the list as a whole.
   * Taking the first N of everything-not-finished hid queued files behind
   * other queued files, so a large batch looked like it had lost most of its
   * work -- unfinished files simply vanished from the list. Anything moving or
   * broken is always shown; only the waiting queue and the finished pile are
   * ever truncated, and both say how many are missing.
   */
  const ROW_LIMIT = 200;

  const inProgress = items.filter(
    (i) => ACTIVE_STATUSES.has(i.status) && i.status !== 'queued',
  );
  const broken = items.filter((i) => i.status === 'error' || i.status === 'cancelled');
  const waiting = items.filter((i) => i.status === 'queued');
  const finished = items.filter((i) => i.status === 'done' || i.status === 'duplicate');

  const visible: typeof items = [];
  for (const group of [inProgress, broken, waiting, finished]) {
    for (const entry of group) {
      if (visible.length >= ROW_LIMIT) break;
      visible.push(entry);
    }
  }

  const shownIds = new Set(visible.map((i) => i.id));
  const hiddenWaiting = waiting.filter((i) => !shownIds.has(i.id)).length;
  const hiddenFinished = finished.filter((i) => !shownIds.has(i.id)).length;

  return (
    <div className="body no-sidebar">
      <div className="main" style={{ maxWidth: 900, margin: '0 auto', width: '100%' }}>
        <h2>Upload media</h2>

        <div className="panel">
          <h3>Destination</h3>
          <FolderPicker
            value={folderId}
            onChange={chooseDestination}
            canCreate={canCreateFolders}
          />

          <div className="flex" style={{ marginTop: 12, alignItems: 'center' }}>
            <label htmlFor="concurrency" style={{ fontSize: 13 }}>
              Files at once
            </label>
            <select
              id="concurrency"
              value={concurrency}
              onChange={(e) => setConcurrency(Number(e.target.value))}
              style={{ maxWidth: 90 }}
            >
              {CONCURRENCY_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>
              Higher helps most with many small files, or when the link is faster than any
              one transfer. Takes effect immediately, including mid-upload.
            </span>
          </div>
        </div>

        <div
          className={`dropzone${dragging ? ' over' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            // Copy what we need before awaiting: the DataTransfer is neutered
            // once the event handler returns.
            const transfer = e.dataTransfer;
            setScanning(true);
            void fromDrop(transfer)
              .then(addCandidates)
              .finally(() => setScanning(false));
          }}
          onClick={() => inputRef.current?.click()}
        >
          <p style={{ margin: 0, fontSize: 15 }}>
            {scanning ? 'Reading folder…' : 'Drop files or folders here'}
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 12 }}>
            Dropped folders keep their structure. Large files upload in resumable chunks,
            and duplicates are detected automatically.
          </p>
          <div className="flex" style={{ justifyContent: 'center', marginTop: 10 }}>
            <button
              type="button"
              className="btn sm"
              onClick={(e) => {
                e.stopPropagation();
                inputRef.current?.click();
              }}
            >
              Choose files
            </button>
            <button
              type="button"
              className="btn sm"
              onClick={(e) => {
                e.stopPropagation();
                folderInputRef.current?.click();
              }}
            >
              Choose a folder
            </button>
          </div>
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files?.length) addCandidates(fromDirectoryInput(e.target.files));
              e.target.value = '';
            }}
          />
          <input
            ref={folderInputRef}
            type="file"
            hidden
            // Non-standard but universally implemented, and the only way to
            // pick a folder. React needs these lowercased as raw attributes.
            {...{ webkitdirectory: '', directory: '' }}
            onChange={(e) => {
              if (e.target.files?.length) addCandidates(fromDirectoryInput(e.target.files));
              e.target.value = '';
            }}
          />
        </div>

        {items.length > 0 && (
          <>
            <div className="flex" style={{ margin: '16px 0 8px' }}>
              <strong>
                {items.filter((i) => i.status === 'done' || i.status === 'duplicate').length} of{' '}
                {items.length} complete
              </strong>
              {failedCount > 0 && (
                <span style={{ color: 'var(--danger)', fontSize: 12 }}>
                  {failedCount} failed
                </span>
              )}
              <div className="spacer" />
              <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>
                {formatBytes(sentBytes)} / {formatBytes(totalBytes)}
              </span>
              {active.length > 0 && (
                <button
                  className="btn sm danger"
                  onClick={() => {
                    if (confirm(`Cancel ${active.length} upload(s) still in progress?`)) {
                      cancelAll();
                    }
                  }}
                >
                  Cancel all
                </button>
              )}
              {failedCount > 0 && (
                <button className="btn sm" onClick={retryAllFailed}>
                  Retry {failedCount}
                </button>
              )}
              <button className="btn sm" onClick={clearFinished} disabled={active.length === items.length}>
                Clear finished
              </button>
            </div>

            <table>
              <thead>
                <tr>
                  <th>File</th>
                  <th style={{ width: 90 }}>Size</th>
                  <th style={{ width: 200 }}>Progress</th>
                  <th style={{ width: 160 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((item) => {
                  const pct = item.file.size > 0 ? (item.sent / item.file.size) * 100 : 0;
                  return (
                    <tr key={item.id}>
                      <td>
                        <div style={{ overflowWrap: 'anywhere' }}>
                          {item.segments.length > 0 && (
                            <span style={{ color: 'var(--text-dim)' }}>
                              {item.segments.join(' / ')} /{' '}
                            </span>
                          )}
                          {item.file.name}
                        </div>
                        {item.message && (
                          <div
                            style={{
                              fontSize: 11,
                              color:
                                item.status === 'error' ? 'var(--danger)' : 'var(--text-dim)',
                            }}
                          >
                            {item.message}
                          </div>
                        )}
                      </td>
                      <td>{formatBytes(item.file.size)}</td>
                      <td>
                        <div className="progress">
                          <div
                            style={{
                              width: `${item.status === 'duplicate' ? 100 : pct}%`,
                              background:
                                item.status === 'error' ? 'var(--danger)' : 'var(--accent)',
                            }}
                          />
                        </div>
                      </td>
                      <td>
                        {item.assetId ? (
                          <Link to={`/asset/${item.assetId}`} style={{ color: 'var(--accent)' }}>
                            {item.status === 'duplicate' ? 'duplicate' : 'view'}
                          </Link>
                        ) : item.status === 'error' || item.status === 'cancelled' ? (
                          <button
                            className="btn sm"
                            onClick={() => retry(item.id)}
                            title={item.message ?? 'Retry this upload'}
                          >
                            Retry
                          </button>
                        ) : ACTIVE_STATUSES.has(item.status) ? (
                          // Both, not one or the other: the status is the only
                          // place hashing and finalizing are visible, and those
                          // are the phases where nothing appears to be moving.
                          <span className="flex" style={{ gap: 6, alignItems: 'center' }}>
                            <span style={{ color: 'var(--text-dim)' }}>{item.status}</span>
                            <button
                              className="btn sm"
                              onClick={() => cancel(item.id)}
                              title="Stop this upload"
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          item.status
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {(hiddenWaiting > 0 || hiddenFinished > 0) && (
              <p style={{ color: 'var(--text-dim)', fontSize: 12, marginTop: 8 }}>
                {[
                  hiddenWaiting > 0 ? `${hiddenWaiting.toLocaleString()} still waiting` : null,
                  hiddenFinished > 0 ? `${hiddenFinished.toLocaleString()} finished` : null,
                ]
                  .filter(Boolean)
                  .join(' and ')}{' '}
                not shown. Everything uploading or needing attention is always listed.
              </p>
            )}
          </>
        )}

        {active.length === 0 && items.length > 0 && (
          <div className="notice success" style={{ marginTop: 16 }}>
            All transfers finished. Thumbnails, metadata and AI tagging continue in the
            background — refresh Browse in a moment to see them.
          </div>
        )}
      </div>
    </div>
  );
}
